import React, { useState, useEffect } from 'react';
import { Lock, TrendingUp, Users, Activity, BarChart3, Download, Trash2, Eye, EyeOff, LogOut, Calendar, Clock, Monitor, UserPlus, RefreshCw, Copy, CheckCircle, XCircle, BookOpen, Music } from 'lucide-react';
import analyticsService from './services/analytics';
import { getUsers, createUser, updateUser, deleteUser as deleteUserFromDb, resetPassword, signInAdmin, getUsageEvents, clearUsageEvents } from './services/supabase';
import { computeStatistics, buildSessions } from './lib/usageStats';

// The dashboard shows the last 90 days of the central usage log.
const HISTORY_DAYS = 90;
const sinceIso = () => new Date(Date.now() - HISTORY_DAYS * 86400000).toISOString();

// Shown when the cloud log cannot be read, so the numbers are never silently wrong.
const LocalOnlyNotice = ({ t, onRetry }) => (
  <div className="max-w-7xl mx-auto mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-center justify-between gap-3">
    <p className={`text-sm ${t.textPrimary}`}>
      Showing this browser's data only. The central usage log could not be read. Run
      {' '}<code>supabase/migration_usage_events.sql</code> in Supabase if you have not yet, and check you are signed in as admin.
    </p>
    <button onClick={onRetry} className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-500">
      Retry
    </button>
  </div>
);

// Reads the same darkMode flag ChurchApp persists (sleekHearSettings), so
// the admin screen follows the operator's chosen theme instead of always
// forcing dark mode.
function useAdminTheme() {
  const [dm, setDm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('sleekHearSettings') || '{}');
      return saved.darkMode !== false;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'sleekHearSettings') return;
      try {
        setDm(JSON.parse(e.newValue || '{}').darkMode !== false);
      } catch {
        // ignore malformed storage payloads
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const t = {
    pageBg:      dm ? 'bg-slate-900'                     : 'bg-gray-50',
    cardBg:      dm ? 'bg-slate-800 border-slate-700'    : 'bg-white border-gray-200',
    innerCardBg: dm ? 'bg-slate-950'                     : 'bg-gray-50',
    innerCardBorder: dm ? 'border-slate-700'             : 'border-gray-200',
    inputBg:     dm ? 'bg-slate-950 text-slate-50 border-slate-700' : 'bg-white text-gray-900 border-gray-300',
    textPrimary: dm ? 'text-slate-50'   : 'text-gray-900',
    textSecondary:dm? 'text-slate-400'  : 'text-gray-500',
    textMuted:   dm ? 'text-slate-500'  : 'text-gray-400',
    rowHover:    dm ? 'hover:bg-slate-700' : 'hover:bg-gray-100',
    divider:     dm ? 'border-slate-700' : 'border-gray-200',
    scrollbar:   dm ? 'scrollbar-thumb-slate-700' : 'scrollbar-thumb-gray-300',
  };

  return { dm, t };
}

const AdminDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('cloud'); // 'cloud' = every login, 'local' = this browser only
  const [cloudRows, setCloudRows] = useState([]);
  const { dm, t } = useAdminTheme();

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const rows = await getUsageEvents(sinceIso(), ['event']);
      setCloudRows(rows);
      setStats(computeStatistics(rows));
      setSource('cloud');
    } catch (error) {
      console.error('Failed to load the central usage log, showing this browser only:', error);
      try {
        setStats(analyticsService.getStatistics());
      } catch (localError) {
        console.error('Failed to load statistics:', localError);
      }
      setSource('local');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (source !== 'cloud') {
      analyticsService.exportAnalytics();
      return;
    }
    const blob = new Blob([JSON.stringify(cloudRows, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sleekHear-usage-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = async () => {
    const what = source === 'cloud' ? 'the usage log for every login' : "this browser's analytics";
    if (!window.confirm(`Are you sure you want to clear ${what}? This cannot be undone.`)) return;
    try {
      if (source === 'cloud') await clearUsageEvents(['event']);
      else analyticsService.clearAnalytics();
    } catch (error) {
      window.alert(`Could not clear the data: ${error.message}`);
    }
    loadStatistics();
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${t.pageBg}`}>
        <div className={`text-xl ${t.textPrimary}`}>Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${t.pageBg}`}>
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className={`flex items-center justify-between rounded-xl p-6 shadow-lg ${dm ? 'bg-gradient-to-r from-brand-900/40 to-slate-900/60 border border-slate-700' : 'bg-white border border-gray-200'}`}>
          <div>
            <h1 className={`text-3xl font-bold flex items-center gap-3 ${t.textPrimary}`}>
              <Lock className={`w-8 h-8 ${t.textSecondary}`} />
              Admin Dashboard
            </h1>
            <p className={`mt-1 ${t.textSecondary}`}>
              SleekHear Analytics & Statistics
              {source === 'cloud' && ` · every login, last ${HISTORY_DAYS} days`}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-slate-50 rounded-lg hover:from-blue-500 hover:to-cyan-500 shadow-lg transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Data
            </button>
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-slate-50 rounded-lg hover:from-red-500 hover:to-red-600 shadow-lg shadow-red-500/30 transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Clear Data
            </button>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-slate-50 rounded-lg hover:from-gray-500 hover:to-gray-600 shadow-lg transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {source === 'local' && <LocalOnlyNotice t={t} onRetry={loadStatistics} />}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className={`flex gap-2 rounded-lg p-2 ${dm ? 'bg-gradient-to-r from-brand-900/20 to-slate-900/40 border border-slate-700' : 'bg-white border border-gray-200'}`}>
          {['overview', 'users', 'user-sessions', 'activity', 'popular', 'sessions', 'events'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all text-sm ${
                activeTab === tab
                  ? 'bg-gradient-to-r from-brand-600 to-brand-500 text-slate-50 shadow-lg'
                  : `${t.textSecondary} ${t.rowHover}`
              }`}
            >
              {tab === 'user-sessions' ? 'User Sessions' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {activeTab === 'overview' && <OverviewTab stats={stats} dm={dm} t={t} />}
        {activeTab === 'users' && <UsersTab dm={dm} t={t} />}
        {activeTab === 'user-sessions' && <UserSessionsTab dm={dm} t={t} />}
        {activeTab === 'activity' && <ActivityTab stats={stats} dm={dm} t={t} />}
        {activeTab === 'popular' && <PopularTab stats={stats} dm={dm} t={t} />}
        {activeTab === 'sessions' && <SessionsTab stats={stats} dm={dm} t={t} />}
        {activeTab === 'events' && <EventsTab stats={stats} dm={dm} t={t} />}
      </div>
    </div>
  );
};

// User Sessions Tab
const UserSessionsTab = ({ dm, t }) => {
  const [sessions, setSessions] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUserId, setFilterUserId] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [cloud, setCloud] = useState(true); // false = fell back to this browser's own history

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    let profiles = [];
    try {
      profiles = await getUsers();
    } catch {
      profiles = [];
    }
    setUsers(profiles);
    try {
      const rows = await getUsageEvents(sinceIso(), ['session', 'activity']);
      setSessions(buildSessions(rows, profiles));
      setCloud(true);
    } catch (error) {
      console.error('Failed to load the central session log, showing this browser only:', error);
      const storedSessions = JSON.parse(localStorage.getItem('sleekHearSessions') || '[]');
      // Sort by login time (newest first)
      storedSessions.sort((a, b) => new Date(b.loginTime) - new Date(a.loginTime));
      setSessions(storedSessions);
      setCloud(false);
    }
  };

  const clearAllSessions = async () => {
    if (!window.confirm('Are you sure you want to clear all session history? This action cannot be undone.')) return;
    try {
      if (cloud) await clearUsageEvents(['session', 'activity']);
      else localStorage.setItem('sleekHearSessions', '[]');
    } catch (error) {
      window.alert(`Could not clear the history: ${error.message}`);
    }
    loadSessions();
  };

  const getSessionDuration = (loginTime, logoutTime) => {
    if (!logoutTime) return 'Active';
    const duration = new Date(logoutTime) - new Date(loginTime);
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  const filteredSessions = sessions.filter(session => {
    if (filterUserId !== 'all' && session.userId !== filterUserId) return false;
    if (filterStatus !== 'all' && session.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`bg-gradient-to-br from-cyan-900/40 to-blue-900/40 rounded-xl p-6 border ${t.innerCardBorder} shadow-lg`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-2xl font-bold ${t.textPrimary} flex items-center gap-2`}>
              <Activity className={`w-6 h-6 ${t.textSecondary}`} />
              User Sessions Monitor
            </h3>
            <p className={`${t.textSecondary} mt-1`}>
              Track login activity and session history
              {cloud ? ` · every login, last ${HISTORY_DAYS} days` : " · this browser only (central log unavailable)"}
            </p>
          </div>
          <button
            onClick={clearAllSessions}
            className={`px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 ${t.textPrimary} rounded-lg hover:from-red-500 hover:to-red-600 font-semibold shadow-lg transition-all transform hover:scale-105 flex items-center gap-2`}
          >
            <Trash2 className="w-4 h-4" />
            Clear History
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border ${t.innerCardBorder}`}>
          <label className={`block ${t.textSecondary} text-sm font-semibold mb-2`}>Filter by User</label>
          <select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            className={`w-full ${t.innerCardBg} ${t.textPrimary} px-3 py-2 rounded-lg border ${t.innerCardBorder} focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none`}
          >
            <option value="all">All Users</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username || 'Unknown'} ({user.loginCode})
              </option>
            ))}
          </select>
        </div>
        <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 border ${t.innerCardBorder}`}>
          <label className={`block ${t.textSecondary} text-sm font-semibold mb-2`}>Filter by Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={`w-full ${t.innerCardBg} ${t.textPrimary} px-3 py-2 rounded-lg border ${t.innerCardBorder} focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none`}
          >
            <option value="all">All Sessions</option>
            <option value="active">Active Only</option>
            <option value="ended">Ended Only</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border ${t.innerCardBorder}`}>
          <div className={`${t.textSecondary} text-sm mb-1`}>Total Sessions</div>
          <div className={`text-3xl font-bold ${t.textPrimary}`}>{sessions.length}</div>
        </div>
        <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 rounded-xl p-6 border border-green-700/30">
          <div className={`${t.textSecondary} text-sm mb-1`}>Active Sessions</div>
          <div className="text-3xl font-bold text-green-400">{sessions.filter(s => s.status === 'active').length}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-900/40 to-cyan-900/40 rounded-xl p-6 border border-blue-700/30">
          <div className={`${t.textSecondary} text-sm mb-1`}>Ended Sessions</div>
          <div className="text-3xl font-bold text-blue-400">{sessions.filter(s => s.status === 'ended').length}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-900/40 to-pink-900/40 rounded-xl p-6 border border-purple-700/30">
          <div className={`${t.textSecondary} text-sm mb-1`}>Filtered Results</div>
          <div className="text-3xl font-bold text-purple-400">{filteredSessions.length}</div>
        </div>
      </div>

      {/* Sessions List */}
      <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border ${t.innerCardBorder} shadow-lg`}>
        <h4 className={`text-xl font-bold ${t.textPrimary} mb-4`}>Session History</h4>

        {filteredSessions.length === 0 ? (
          <div className={`text-center py-12 ${t.textSecondary}`}>
            <Activity className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No sessions found</p>
            <p className="text-sm mt-2">User login sessions will appear here</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {filteredSessions.map((session) => (
              <div
                key={session.id}
                className={`bg-gradient-to-r ${
                  session.status === 'active'
                    ? 'from-green-900/20 to-emerald-900/20 border-green-700/30'
                    : 'from-slate-900/80 to-slate-800/80 border-slate-700'
                } rounded-lg p-4 border`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`${t.textPrimary} font-bold text-lg`}>
                        {session.username || 'Unknown User'}
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${
                        session.status === 'active'
                          ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                          : 'bg-slate-600/20 text-slate-400 border border-slate-600/30'
                      }`}>
                        {session.status === 'active' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {session.status === 'active' ? 'ACTIVE' : 'ENDED'}
                      </div>
                    </div>
                    <div className={`text-xs ${t.textSecondary} space-y-1`}>
                      <div>Login Code: <code className="text-blue-400 font-mono">{session.loginCode}</code></div>
                      <div>Session ID: <code className={`${t.textMuted} font-mono text-xs`}>{session.id}</code></div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-1`}>Login Time</div>
                    <div className={`${t.textPrimary} text-sm font-semibold`}>
                      {new Date(session.loginTime).toLocaleString()}
                    </div>
                  </div>
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-1`}>Logout Time</div>
                    <div className={`${t.textPrimary} text-sm font-semibold`}>
                      {session.logoutTime ? new Date(session.logoutTime).toLocaleString() : 'Still active'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-1`}>Duration</div>
                    <div className={`${t.textPrimary} text-sm font-semibold`}>
                      {getSessionDuration(session.loginTime, session.logoutTime)}
                    </div>
                  </div>
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-1`}>Platform</div>
                    <div className={`${t.textPrimary} text-sm font-semibold truncate`}>
                      {session.platform}
                    </div>
                  </div>
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-1`}>Browser</div>
                    <div className={`${t.textPrimary} text-sm font-semibold truncate`}>
                      {session.userAgent.includes('Chrome') ? 'Chrome' :
                       session.userAgent.includes('Firefox') ? 'Firefox' :
                       session.userAgent.includes('Safari') ? 'Safari' :
                       session.userAgent.includes('Edge') ? 'Edge' : 'Other'}
                    </div>
                  </div>
                </div>

                {/* Activities Section */}
                {session.activities && session.activities.length > 0 && (
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-2 font-semibold flex items-center gap-2`}>
                      <Activity className="w-3 h-3" />
                      Session Activities ({session.activities.length})
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                      {session.activities.map((activity, idx) => (
                        <div key={idx} className="bg-slate-900 rounded p-2 text-xs border border-slate-800">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <span className="text-blue-400 font-semibold">
                              {activity.type.replace(/_/g, ' ').toUpperCase()}
                            </span>
                            <span className={`${t.textMuted} text-[10px]`}>
                              {new Date(activity.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          {activity.details && Object.keys(activity.details).length > 0 && (
                            <div className={`${t.textSecondary} text-[11px] space-y-0.5`}>
                              {Object.entries(activity.details).map(([key, value]) => (
                                <div key={key}>
                                  <span className={`${t.textMuted}`}>{key}:</span>{' '}
                                  <span className="text-slate-300">{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Users Tab
const UsersTab = ({ dm, t }) => {
  const [users, setUsers] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userError, setUserError] = useState(null);
  // Freshly generated credentials, shown exactly once (passwords are never
  // stored readable — they live hashed in Supabase Auth).
  const [newCredentials, setNewCredentials] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    setUserError(null);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setUserError('Failed to load users. Check your Supabase configuration.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const createNewUser = async () => {
    setCreating(true);
    setUserError(null);
    try {
      const { user: saved, password } = await createUser();
      setUsers(prev => [...prev, saved]);
      setNewCredentials({ loginCode: saved.loginCode, password, username: saved.username });
    } catch (err) {
      setUserError('Failed to create user.');
    } finally {
      setCreating(false);
    }
  };

  const updateUsername = async (userId, newUsername) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, username: newUsername } : u));
    try {
      await updateUser(userId, { username: newUsername });
    } catch (err) {
      setUserError('Failed to save username.');
    }
  };

  const toggleUserStatus = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const updated = { ...user, isActive: !user.isActive };
    setUsers(prev => prev.map(u => u.id === userId ? updated : u));
    try {
      await updateUser(userId, { isActive: updated.isActive });
    } catch (err) {
      setUserError('Failed to update user status.');
      setUsers(prev => prev.map(u => u.id === userId ? user : u));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      setUsers(prev => prev.filter(u => u.id !== userId));
      try {
        await deleteUserFromDb(userId);
      } catch (err) {
        setUserError('Failed to delete user.');
        loadUsers();
      }
    }
  };

  const handleResetPassword = async (userId) => {
    if (window.confirm('Generate a new password for this user? The old password will no longer work.')) {
      try {
        const user = users.find(u => u.id === userId);
        const password = await resetPassword(userId);
        setNewCredentials({ loginCode: user?.loginCode, password, username: user?.username });
      } catch (err) {
        setUserError('Failed to reset password.');
      }
    }
  };

  const copyToClipboard = (text, code) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  if (loadingUsers) {
    return (
      <div className={`flex items-center justify-center py-20 ${t.textSecondary}`}>
        <div className="w-8 h-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-3"></div>
        Loading users...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className={`bg-gradient-to-br from-blue-900/40 to-indigo-900/40 rounded-xl p-6 border ${t.innerCardBorder} shadow-lg`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-2xl font-bold ${t.textPrimary} flex items-center gap-2`}>
              <Users className={`w-6 h-6 ${t.textSecondary}`} />
              User Management
            </h3>
            <p className={`${t.textSecondary} mt-1`}>Create and manage user login credentials</p>
          </div>
          <button
            onClick={createNewUser}
            disabled={creating}
            className={`px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 ${t.textPrimary} rounded-lg hover:from-green-500 hover:to-emerald-500 font-bold shadow-lg transition-all transform hover:scale-105 flex items-center gap-2 disabled:opacity-50 disabled:transform-none`}
          >
            <UserPlus className="w-5 h-5" />
            {creating ? 'Creating...' : 'Generate New User'}
          </button>
        </div>
      </div>

      {userError && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {userError}
        </div>
      )}

      {newCredentials && (
        <div className="p-5 bg-amber-500/10 border border-amber-500/40 rounded-lg">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-amber-400 font-bold mb-2">
                New credentials{newCredentials.username ? ` for ${newCredentials.username}` : ''} — copy them now, the password is shown only once
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <span className={`${t.textSecondary}`}>Login Code: </span>
                  <code className={`${t.textPrimary} font-mono font-bold`}>{newCredentials.loginCode}</code>
                </div>
                <div>
                  <span className={`${t.textSecondary}`}>Password: </span>
                  <code className={`${t.textPrimary} font-mono font-bold`}>{newCredentials.password}</code>
                </div>
                <button
                  onClick={() => copyToClipboard(`Login code: ${newCredentials.loginCode}\nPassword: ${newCredentials.password}`, 'new-creds')}
                  className={`px-2 py-1 rounded flex items-center gap-1 ${t.innerCardBg} ${t.rowHover} ${t.textPrimary}`}
                >
                  {copiedCode === 'new-creds' ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  Copy both
                </button>
              </div>
            </div>
            <button
              onClick={() => setNewCredentials(null)}
              className={`px-3 py-1 rounded text-sm ${t.innerCardBg} ${t.rowHover} ${t.textSecondary}`}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border ${t.innerCardBorder}`}>
          <div className={`${t.textSecondary} text-sm mb-1`}>Total Users</div>
          <div className={`text-3xl font-bold ${t.textPrimary}`}>{users.length}</div>
        </div>
        <div className="bg-gradient-to-br from-green-900/40 to-emerald-900/40 rounded-xl p-6 border border-green-700/30">
          <div className={`${t.textSecondary} text-sm mb-1`}>Active Users</div>
          <div className="text-3xl font-bold text-green-400">{users.filter(u => u.isActive).length}</div>
        </div>
        <div className="bg-gradient-to-br from-red-900/40 to-orange-900/40 rounded-xl p-6 border border-red-700/30">
          <div className={`${t.textSecondary} text-sm mb-1`}>Inactive Users</div>
          <div className="text-3xl font-bold text-red-400">{users.filter(u => !u.isActive).length}</div>
        </div>
      </div>

      {/* Users List */}
      <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 border ${t.innerCardBorder} shadow-lg`}>
        <h4 className={`text-xl font-bold ${t.textPrimary} mb-4`}>All Users</h4>

        {users.length === 0 ? (
          <div className={`text-center py-12 ${t.textSecondary}`}>
            <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No users created yet</p>
            <p className="text-sm mt-2">Click "Generate New User" to create login credentials</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
            {users.map((user) => (
              <div
                key={user.id}
                className={`bg-gradient-to-r ${
                  user.isActive
                    ? 'from-slate-900/80 to-slate-800/80 border-slate-700'
                    : 'from-red-900/20 to-red-800/20 border-red-700/30'
                } rounded-lg p-4 border`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`p-2 ${user.isActive ? 'bg-green-600' : 'bg-red-600'} rounded-lg`}>
                      {user.isActive ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        <XCircle className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={user.username || ''}
                        onChange={(e) => updateUsername(user.id, e.target.value)}
                        placeholder="Enter username (e.g., John Doe)"
                        className={`w-full ${t.innerCardBg} ${t.textPrimary} font-bold text-base px-3 py-1.5 rounded-lg border ${t.innerCardBorder} focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 outline-none placeholder-slate-600`}
                      />
                      <div className={`text-xs ${t.textSecondary} mt-1`}>
                        ID: {user.id} • Created: {new Date(user.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                    user.isActive
                      ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                      : 'bg-red-600/20 text-red-400 border border-red-600/30'
                  }`}>
                    {user.isActive ? 'ACTIVE' : 'INACTIVE'}
                  </div>
                </div>

                {/* Credentials */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-1`}>Login Code</div>
                    <div className="flex items-center justify-between gap-2">
                      <code className={`${t.textPrimary} font-mono font-bold text-lg`}>{user.loginCode}</code>
                      <button
                        onClick={() => copyToClipboard(user.loginCode, `code-${user.id}`)}
                        className={`p-2 rounded transition-all ${t.rowHover}`}
                        title="Copy to clipboard"
                      >
                        {copiedCode === `code-${user.id}` ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className={`w-4 h-4 ${t.textSecondary}`} />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
                    <div className={`text-xs ${t.textSecondary} mb-1`}>Password</div>
                    <div className={`${t.textMuted} text-sm`}>
                      Stored securely — use "Reset Password" to issue a new one
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleUserStatus(user.id)}
                    className={`flex-1 px-3 py-2 ${
                      user.isActive
                        ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500'
                    } text-white rounded-lg text-sm font-semibold transition-all`}
                  >
                    {user.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleResetPassword(user.id)}
                    className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Reset Password
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="px-4 py-2 bg-gradient-to-r from-red-700 to-red-800 hover:from-red-600 hover:to-red-700 text-white rounded-lg text-sm font-semibold transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Overview Tab
const OverviewTab = ({ stats, dm, t }) => {
  const statCards = [
    { label: 'Total Sessions', value: stats.overview.totalSessions, icon: Users, color: 'bg-brand-600' },
    { label: 'Total Events', value: stats.overview.totalEvents, icon: Activity, color: 'from-purple-500 to-pink-500' },
    { label: 'Avg Events/Session', value: stats.overview.avgEventsPerSession, icon: TrendingUp, color: 'bg-emerald-600' },
    { label: 'Display Opens', value: stats.statistics.displayWindowOpened, icon: Monitor, color: 'from-amber-500 to-orange-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, idx) => (
          <div key={idx} className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
            <div className="flex items-center justify-between mb-3">
              <div className={`p-3 bg-gradient-to-br ${stat.color} rounded-lg`}>
                <stat.icon className={`w-6 h-6 ${t.textPrimary}`} />
              </div>
              <div className="text-right">
                <div className={`text-3xl font-bold ${t.textPrimary}`}>{stat.value}</div>
                <div className={`text-sm ${t.textSecondary}`}>{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feature Usage */}
      <div className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
        <h3 className={`text-xl font-bold ${t.textPrimary} mb-4 flex items-center gap-2`}>
          <BarChart3 className={`w-5 h-5 ${t.textSecondary}`} />
          Feature Usage Statistics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatItem label="Bible Searches" value={stats.statistics.bibleSearches} t={t} />
          <StatItem label="Song Searches" value={stats.statistics.songSearches} t={t} />
          <StatItem label="Verses Displayed" value={stats.statistics.versesDisplayed} t={t} />
          <StatItem label="Songs Displayed" value={stats.statistics.songsDisplayed} t={t} />
          <StatItem label="Voice Search Used" value={stats.statistics.voiceSearchUsed} t={t} />
          <StatItem label="AI Song Search" value={stats.statistics.aiSongSearchUsed} t={t} />
          <StatItem label="Settings Changed" value={stats.statistics.settingsChanged} t={t} />
          <StatItem label="Display Windows" value={stats.statistics.displayWindowOpened} t={t} />
        </div>
      </div>
    </div>
  );
};

// Activity Tab
const ActivityTab = ({ stats, dm, t }) => {
  const dailyActivity = Object.entries(stats.recentActivity.daily || {})
    .sort((a, b) => b[0].localeCompare(a[0]))
    .slice(0, 30);

  const monthlyActivity = Object.entries(stats.recentActivity.monthly || {})
    .sort((a, b) => b[0].localeCompare(a[0]));

  return (
    <div className="space-y-6">
      {/* Daily Activity */}
      <div className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
        <h3 className={`text-xl font-bold ${t.textPrimary} mb-4 flex items-center gap-2`}>
          <Calendar className={`w-5 h-5 ${t.textSecondary}`} />
          Daily Activity (Last 30 Days)
        </h3>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {dailyActivity.map(([date, data]) => (
            <div key={date} className={`flex items-center justify-between ${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
              <span className={`${t.textSecondary} font-semibold`}>{date}</span>
              <span className={`${t.textPrimary} font-bold`}>{data.count} events</span>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Activity */}
      <div className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
        <h3 className={`text-xl font-bold ${t.textPrimary} mb-4 flex items-center gap-2`}>
          <Calendar className={`w-5 h-5 ${t.textSecondary}`} />
          Monthly Activity
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {monthlyActivity.map(([month, data]) => (
            <div key={month} className={`${t.innerCardBg} rounded-lg p-4 border ${t.innerCardBorder}`}>
              <div className={`${t.textSecondary} font-semibold mb-1`}>{month}</div>
              <div className={`text-2xl font-bold ${t.textPrimary}`}>{data.count} events</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Popular Tab
const PopularTab = ({ stats, dm, t }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Popular Verses */}
      <div className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
        <h3 className={`text-xl font-bold ${t.textPrimary} mb-4 flex items-center gap-2`}><BookOpen className="w-5 h-5 text-blue-400" /> Most Displayed Verses</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {stats.popularVerses.length > 0 ? (
            stats.popularVerses.map((verse, idx) => (
              <div key={idx} className={`flex items-center justify-between bg-gradient-to-r from-blue-900/30 to-cyan-900/30 rounded-lg p-3 border ${t.innerCardBorder}`}>
                <span className={`${t.textPrimary} font-semibold`}>{verse.name}</span>
                <span className={`${t.textPrimary} font-bold`}>{verse.count}x</span>
              </div>
            ))
          ) : (
            <div className={`${t.textSecondary} text-center py-4`}>No data yet</div>
          )}
        </div>
      </div>

      {/* Popular Songs */}
      <div className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
        <h3 className={`text-xl font-bold ${t.textPrimary} mb-4 flex items-center gap-2`}><Music className="w-5 h-5 text-emerald-400" /> Most Displayed Songs</h3>
        <div className="space-y-2 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          {stats.popularSongs.length > 0 ? (
            stats.popularSongs.map((song, idx) => (
              <div key={idx} className={`flex items-center justify-between bg-gradient-to-r from-green-900/30 to-emerald-900/30 rounded-lg p-3 border ${t.innerCardBorder}`}>
                <span className="text-emerald-600 font-semibold">{song.name}</span>
                <span className={`${t.textPrimary} font-bold`}>{song.count}x</span>
              </div>
            ))
          ) : (
            <div className={`${t.textSecondary} text-center py-4`}>No data yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Sessions Tab
const SessionsTab = ({ stats, dm, t }) => {
  return (
    <div className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
      <h3 className={`text-xl font-bold ${t.textPrimary} mb-4 flex items-center gap-2`}>
        <Users className={`w-5 h-5 ${t.textSecondary}`} />
        Recent Sessions (Last 50)
      </h3>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
        {stats.sessions.map((session, idx) => (
          <div key={idx} className={`${t.innerCardBg} rounded-lg p-4 border ${t.innerCardBorder}`}>
            <div className="flex items-start justify-between mb-2">
              <div className={`${t.textSecondary} font-mono text-sm`}>{session.sessionId}</div>
              <div className={`text-xs ${t.textSecondary} flex items-center gap-1`}>
                <Clock className="w-3 h-3" />
                {new Date(session.startTime).toLocaleString()}
              </div>
            </div>
            <div className={`text-xs ${t.textSecondary}`}>
              <div>Platform: {session.platform}</div>
              <div className="truncate">User Agent: {session.userAgent}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Events Tab
const EventsTab = ({ stats, dm, t }) => {
  return (
    <div className={`${t.cardBg} rounded-xl p-6 border shadow-lg`}>
      <h3 className={`text-xl font-bold ${t.textPrimary} mb-4 flex items-center gap-2`}>
        <Activity className={`w-5 h-5 ${t.textSecondary}`} />
        Recent Events (Last 100)
      </h3>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
        {stats.recentEvents.map((event, idx) => (
          <div key={idx} className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
            <div className="flex items-start justify-between mb-1">
              <div className={`${t.textPrimary} font-semibold`}>{event.name}</div>
              <div className={`text-xs ${t.textSecondary}`}>{new Date(event.timestamp).toLocaleString()}</div>
            </div>
            {Object.keys(event.data || {}).length > 0 && (
              <div className={`text-xs ${t.textSecondary} mt-2`}>
                {JSON.stringify(event.data, null, 2)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper Component
const StatItem = ({ label, value, t }) => (
  <div className={`${t.innerCardBg} rounded-lg p-3 border ${t.innerCardBorder}`}>
    <div className={`${t.textSecondary} text-sm mb-1`}>{label}</div>
    <div className={`text-2xl font-bold ${t.textPrimary}`}>{value}</div>
  </div>
);

// Admin Login Component — authenticates against Supabase Auth and requires
// the account to carry the admin role (app_metadata.role = 'admin').
export const AdminLogin = ({ onLogin }) => {
  const { t } = useAdminTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const ok = await signInAdmin(email, password);
      if (ok) {
        onLogin();
      } else {
        setError('Invalid credentials or not an admin account.');
        setPassword('');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-6 ${t.pageBg}`}>
      <div className={`rounded-xl p-8 shadow-lg max-w-md w-full ${t.cardBg} border`}>
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-brand-600 rounded-xl mb-4">
            <Lock className="w-12 h-12 text-slate-50" />
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${t.textPrimary}`}>
            Admin Access
          </h1>
          <p className={t.textSecondary}>Sign in with your admin account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block mb-2 font-semibold ${t.textSecondary}`}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full p-4 rounded-lg border focus:ring-2 focus:ring-brand-500/30 outline-none ${t.inputBg}`}
              placeholder="Enter admin email"
              autoFocus
              required
            />
          </div>
          <div>
            <label className={`block mb-2 font-semibold ${t.textSecondary}`}>Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full p-4 rounded-lg border focus:ring-2 focus:ring-brand-500/30 outline-none ${t.inputBg}`}
                placeholder="Enter admin password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-4 top-1/2 -translate-y-1/2 ${t.textSecondary}`}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {error && (
            <div className={`bg-red-900/30 border ${t.divider} text-red-500 px-4 py-3 rounded-lg`}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-brand-600 to-brand-500 text-slate-50 font-bold rounded-lg hover:from-brand-500 hover:to-blue-400 shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:transform-none"
          >
            {loading ? 'Signing in...' : 'Access Dashboard'}
          </button>
        </form>

        <div className={`mt-6 text-center text-sm flex items-center justify-center gap-1.5 ${t.textSecondary}`}>
          <Lock className="w-3.5 h-3.5" /> Secure admin-only area
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
